// src/encounter/encounter-route.tsx
//
// R18 (PROD-2743): ONE mount per appointment. `encounter/:appointmentId` used to render
// `<EncounterShellPage />` directly, and React Router reuses a route element across a param change,
// so moving from one appointment's encounter to another's (a history jump, a notification link)
// kept the SAME page instance: `encounterId`, `started`, the open panels, the draft response and
// every ref seeded from the first patient survived into the second. Keying the page on the
// appointment id makes a new appointment a new mount, so nothing from the previous patient can
// stay mounted. Within one appointment nothing changes — the key is stable, so tab and step
// changes (search params) never remount.
//
// Applied on BOTH frames, not behind the frame-v2 switch: it is a patient-safety reset, and the
// default frame is the one clinicians use.
//
// D-73 (amended, 6 Oct) · frame v2 only: the in-app leave guard sits beside the page, inside the data router (it uses
// the router's blocker). Read once per mount, like the frame itself; OFF renders the page alone, exactly as before.
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { EncounterShellPage } from './encounter-shell-page';
import { EncounterLeaveGuard } from './encounter-leave-guard';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import { NoteSessionContext, newNoteSession } from './services/note-session';

export function EncounterRoute() {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  return <EncounterMount key={appointmentId} appointmentId={appointmentId} />;
}

/**
 * One appointment's page and its guard, keyed together (integration, Codex P2): the guard reads the switch at the same
 * mount the page does, so moving to an appointment opened with `?ef=frameV2` mounts its guard too (the route element is
 * reused across a param change, and a flag read once per ROUTE stayed false).
 */
function EncounterMount({ appointmentId }: { appointmentId: string | undefined }) {
  const [frameV2On] = useState(() => isEncounterFlagOn('frameV2'));
  // PROD-3107: this mount's note session. Every note write made under it is refused once Discard marks it, including a
  // save paused offline that would resume after this page is gone.
  const [noteSession] = useState(() => newNoteSession(appointmentId));
  // Showcase look, `?ef=encounterPolish` (off by default): the polish rules in showcase-bridge.css hang on this one
  // attribute. `contents` keeps the wrapper out of the layout; OFF mounts no wrapper at all, exactly as before.
  const [polishOn] = useState(() => isEncounterFlagOn('encounterPolish'));
  const page = <EncounterShellPage />;
  return (
    <NoteSessionContext.Provider value={noteSession}>
      {polishOn ? (
        <div className="contents" data-encounter-polish="on">
          {page}
        </div>
      ) : (
        page
      )}
      {frameV2On && <EncounterLeaveGuard />}
    </NoteSessionContext.Provider>
  );
}
